export type InsightBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'quote'; text: string; by?: string }
  | { type: 'list'; items: string[] };

export interface InsightArticle {
  slug: string;
  cat: string;
  title: string;
  meta: string;
  author: string;
  date: string;
  minutes: number;
  excerpt: string;
  img: string;
  featured?: boolean;
  body: InsightBlock[];
}

export const insights: InsightArticle[] = [
  {
    slug: 'what-communities-think',
    cat: 'Public Opinion',
    title: 'What communities actually think: a field-note from a large-sample survey',
    meta: 'Opinion Research · 12 min read',
    author: 'Prabhaw Research Desk',
    date: 'June 2025',
    minutes: 12,
    excerpt:
      'Behind every sample size is a community. This field-note pulls back the curtain on how large-sample opinion research is designed, fielded and read — and what it really takes to hear people.',
    img: 'https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20field%20researcher%20notebook%20with%20handwritten%20notes%20and%20a%20survey%20clipboard%20on%20a%20warm%20desk%2C%20soft%20natural%20light%2C%20deep%20green%20and%20amber%20tones%2C%20authentic%20grounded%20documentary%20mood%2C%20no%20people%20no%20logos&width=800&height=520&seq=prabhaw-insight-01&orientation=landscape',
    featured: true,
    body: [
      {
        type: 'paragraph',
        text: 'Numbers have a strange authority. A percentage point, a margin of error, a confidence interval — they read as certainty. But every large-sample survey is, at its core, a chain of human decisions: who we asked, how we asked, what we assumed people meant, and what they actually meant when they answered. This field-note is an honest account of that chain.',
      },
      {
        type: 'heading',
        text: 'Designing the frame before the questionnaire',
      },
      {
        type: 'paragraph',
        text: 'The most consequential choices are made before a single question is drafted. The sampling frame decides whose voice is even possible to hear. In the work we do across districts and constituencies, the first discipline is stratification that reflects reality — by geography, by remoteness, by economic profile, by the daily rhythms that determine when a household is reachable at all.',
      },
      {
        type: 'paragraph',
        text: 'A survey that samples only the reachable quietly excludes the inaccessible — and the inaccessible are often precisely the people policy most needs to hear. We treat non-response not as a nuisance but as information: who was hard to reach, and what that might tell us about the very question we are asking.',
      },
      {
        type: 'quote',
        text: 'A good sample is not a mirror held to a population. It is a deliberate act of inclusion — a decision about whose experience counts as evidence.',
        by: 'Prabhaw Research Desk',
      },
      {
        type: 'heading',
        text: 'Asking in the words people use',
      },
      {
        type: 'paragraph',
        text: 'Question wording is where opinion research most often fails quietly. Translation is not enough; meaning must be carried. The same policy can be described in the language of entitlements or the language of schemes, and the distribution of answers will move with the framing. Our field teams spend as much time on cognitive testing as on analysis — checking not that a question is grammatical, but that it means the same thing to every respondent.',
      },
      {
        type: 'list',
        items: [
          'Locally relevant phrasing and tested translations across dialects',
          'Short, single-idea questions that reduce acquiescence bias',
          'Enumeration in the respondent\u2019s language, never through an interpreter',
          'Anonymity protocols that let people answer without fear',
        ],
      },
      {
        type: 'heading',
        text: 'Reading the curve, not just the headline',
      },
      {
        type: 'paragraph',
        text: 'The headline number is the least interesting part of most surveys. The curve is where understanding lives — how opinion shifts with age, with distance from services, with gender, with the lived experience of a particular scheme. A single national average can conceal two entirely opposite realities sitting side by side.',
      },
      {
        type: 'paragraph',
        text: 'The discipline we practice is disaggregation: reporting the distribution, the internal tensions, and the minority voices that a rounded aggregate would erase. The most honest summary of a survey is often a contradiction — and learning to hold two truths at once is what separates a poll from an insight.',
      },
      {
        type: 'heading',
        text: 'Returning the finding to the community',
      },
      {
        type: 'paragraph',
        text: 'Research that never returns to the people it came from is extractive. In our engagements, the final and least glamorous step matters most: going back to the field with the findings, testing whether the interpretation rings true to those who gave the answers, and letting them correct us. This is not a courtesy. It is validation.',
      },
      {
        type: 'paragraph',
        text: 'Opinion research done well is a conversation, not a verdict. It tells an institution not what to decide, but what people are already telling each other, in their own words, about their own lives. That is where trust begins.',
      },
    ],
  },
  {
    slug: 'mis-built-for-people',
    cat: 'Governance',
    title: 'From data to delivery: why MIS must be built for people, not just reports',
    meta: 'Analysis · 8 min read',
    author: 'Prabhaw Practice Group',
    date: 'May 2025',
    minutes: 8,
    excerpt:
      'Management information systems fail when they are designed around reports instead of around the people who must use them daily. A practical argument for human-centred MIS in public institutions.',
    img: 'https://readdy.ai/api/search-image?query=Editorial%20abstract%20photograph%20of%20charts%20and%20printed%20dashboards%20beside%20a%20pencil%20on%20a%20minimal%20desk%2C%20deep%20emerald%20and%20amber%20gold%20tones%2C%20governance%20technology%20mood%2C%20soft%20natural%20light%2C%20no%20text%20no%20people%20no%20logos&width=800&height=520&seq=prabhaw-insight-02&orientation=landscape',
    body: [
      {
        type: 'paragraph',
        text: 'Every public delivery system generates data. The question is whether that data ever becomes decisions. Too often, the gap is not technical — it is human. Systems are built for the report, and the report is built for no one in particular. The people closest to delivery, the ones who could act on the information, are the last to be consulted.',
      },
      {
        type: 'heading',
        text: 'The report-first trap',
      },
      {
        type: 'paragraph',
        text: 'Most management information systems begin with a question about output: what reports do we need at the top? The consequence is a dashboard that impresses upward and informs downward almost not at all. Field staff enter data because they must; they never see it come back to them in a form that helps them do their job better the next day.',
      },
      {
        type: 'paragraph',
        text: 'When entry is an obligation and retrieval is a privilege, the quality of data decays. The system becomes a chore, and a chore is done carelessly. Data quality is not a technical problem; it is a motivational one. People protect information that they believe is used and abandon information they believe is collected to be ignored.',
      },
      {
        type: 'quote',
        text: 'Build the system around the person who must act on the data, and the reports will write themselves. Build it around the report, and the data will quietly die.',
      },
      {
        type: 'heading',
        text: 'Designing for the frontline',
      },
      {
        type: 'paragraph',
        text: 'A human-centred MIS starts at the point of service. The field worker who records an enrolment, a delivery, or a grievance should receive immediate, legible feedback: what changed, what still needs attention, what to do next. The interface should reward accuracy with usefulness, not punish entry with boredom.',
      },
      {
        type: 'list',
        items: [
          'Feedback loops that return value to the data entrant',
          'Interfaces built for low-connectivity, real-world field conditions',
          'Clear ownership so every record has a person accountable for it',
          'Analytics that surface anomalies rather than just totals',
        ],
      },
      {
        type: 'heading',
        text: 'Data as a conversation, not a monologue',
      },
      {
        type: 'paragraph',
        text: 'The systems that endure are the ones that talk back. A supervisor sees the pattern; the field worker sees the reason behind it; the two together make a decision. That is the true measure of an MIS — not the elegance of its charts, but whether the flow of information changes the flow of work.',
      },
      {
        type: 'paragraph',
        text: 'In our engagements with public institutions, we have learned that the adoption of a system is won in its first hundred days, in the daily experience of the person entering the tenth record of the morning. If that person feels the system works for them, it will work for everyone. If not, no report will ever save it.',
      },
    ],
  },
  {
    slug: 'the-quiet-signal',
    cat: 'Politics & Society',
    title: 'The quiet signal: reading public mood beyond the headline',
    meta: 'Field Observation · 6 min read',
    author: 'Prabhaw Field Desk',
    date: 'April 2025',
    minutes: 6,
    excerpt:
      'Public mood rarely announces itself in headlines. It accumulates in small, everyday signals — a question asked twice, a silence that lasts too long. How to read it.',
    img: 'https://readdy.ai/api/search-image?query=Editorial%20abstract%20photograph%20of%20layered%20newspaper%20clippings%20and%20notes%20on%20a%20warm%20wooden%20table%20with%20a%20coffee%20cup%2C%20soft%20window%20light%2C%20deep%20green%20and%20amber%20tones%2C%20analytical%20documentary%20mood%2C%20no%20text%20no%20people%20no%20logos&width=800&height=520&seq=prabhaw-insight-03&orientation=landscape',
    body: [
      {
        type: 'paragraph',
        text: 'The loudest opinion is not always the most real. Public mood lives beneath the surface, in the questions people ask each other rather than the ones they shout, in the grievances they mention casually and the hopes they only whisper. Reading it requires a different kind of attention.',
      },
      {
        type: 'heading',
        text: 'The difference between opinion and mood',
      },
      {
        type: 'paragraph',
        text: 'Opinion is a position; mood is a temperature. Opinion can be polled in a single question. Mood must be gathered — from the way a conversation turns, from what is said about a policy by someone it has never touched, from the local story that everyone knows and no one reports. Mood is the weather that opinion is the forecast of.',
      },
      {
        type: 'paragraph',
        text: 'The most reliable mood signals are rarely dramatic. They are the small, repeated, slightly unusual details: a service point where complaints have quietly doubled, a district where migration is spoken of as inevitability, a scheme whose name people know but whose benefit they describe as other people\u2019s.',
      },
      {
        type: 'quote',
        text: 'Trust rarely breaks with a bang. It leaks through a thousand small disconfirmations — and the leaks are visible long before the break, to anyone who reads the quiet signal.',
      },
      {
        type: 'heading',
        text: 'Listening where the noise is low',
      },
      {
        type: 'list',
        items: [
          'Following second-hand stories — the grievance everyone repeats',
          'Watching what is asked of officials that is never written down',
          'Tracking the words people choose, not just the positions they hold',
          'Noting silences: the topics that conversation politely avoids',
        ],
      },
      {
        type: 'heading',
        text: 'The value of an early read',
      },
      {
        type: 'paragraph',
        text: 'An institution that reads the quiet signal can act while a problem is still small, while trust is still recoverable, while a concern is still a preference rather than a grievance. That is the practical payoff of mood research: not prediction, but early, humble, honest awareness of where people actually stand.',
      },
      {
        type: 'paragraph',
        text: 'The headline will always be the last thing we learn. The quiet signal is available much earlier — to anyone willing to listen to the spaces between the words.',
      },
    ],
  },
  {
    slug: 'building-an-intelligence-layer',
    cat: 'Technology',
    title: 'Building an intelligence layer for public institutions',
    meta: 'Technology · 9 min read',
    author: 'Prabhaw Technology Group',
    date: 'March 2025',
    minutes: 9,
    excerpt:
      'Technology for the public sector is not about the newest tool. It is about layering intelligence over existing systems so decisions get faster, fairer and more accountable.',
    img: 'https://readdy.ai/api/search-image?query=Editorial%20abstract%20photograph%20of%20circuit%20patterns%20and%20data%20nodes%20glowing%20in%20deep%20emerald%20green%20and%20amber%20gold%20on%20a%20dark%20surface%2C%20premium%20technology%20aesthetic%2C%20soft%20depth%20of%20field%2C%20no%20text%20no%20people%20no%20logos&width=800&height=520&seq=prabhaw-insight-04&orientation=landscape',
    body: [
      {
        type: 'paragraph',
        text: 'Public institutions do not need more systems. They need better sense-making across the systems they already have. The opportunity of technology is not the latest dashboard or the newest platform; it is an intelligence layer — a thin, persistent layer that turns scattered operational data into insight that a decision-maker can act on this week, not next quarter.',
      },
      {
        type: 'heading',
        text: 'Intelligence is a layer, not an application',
      },
      {
        type: 'paragraph',
        text: 'The temptation is to build one more monolithic application. The wiser path is to build a layer: connectors that read from existing records, models that detect patterns, and interfaces that surface a question in plain language rather than a report in dense tables. The layer does not replace the institution\u2019s systems. It makes them speak to one another.',
      },
      {
        type: 'paragraph',
        text: 'This is a deliberate architectural humility. We do not ask an institution to abandon what works. We ask it to let us add understanding on top. Adoption follows because nothing familiar is taken away.',
      },
      {
        type: 'quote',
        text: 'The scarcest resource in government is not data and not code. It is the attention of the people who must decide. An intelligence layer exists to spend that attention where it matters.',
      },
      {
        type: 'heading',
        text: 'From hindsight to early warning',
      },
      {
        type: 'list',
        items: [
          'Detecting emerging demand patterns before they become backlogs',
          'Flagging anomalies in delivery that a rolling average hides',
          'Correlating inputs with outcomes to reveal what actually works',
          'Nudging action: surfacing the next decision, not just the last result',
        ],
      },
      {
        type: 'heading',
        text: 'Built for accountability',
      },
      {
        type: 'paragraph',
        text: 'An intelligence layer is only as trustworthy as the questions it can answer about itself. Where did this number come from? Who decided what to flag? Can a finding be traced back to a source? We build for auditability from the first line of code, because a tool that cannot explain itself will never be trusted with public decisions.',
      },
      {
        type: 'paragraph',
        text: 'Done well, the layer makes the institution faster without making it reckless, and more accountable without making it slower. That is the quiet ambition of public-sector technology: not spectacle, but the steady, defensible improvement of decisions that touch millions of lives.',
      },
    ],
  },
  {
    slug: 'iec-campaigns',
    cat: 'Communication',
    title: 'Public awareness that sticks: lessons from IEC campaigns',
    meta: 'Communication · 7 min read',
    author: 'Prabhaw Communication Desk',
    date: 'February 2025',
    minutes: 7,
    excerpt:
      'Information, education and communication campaigns fail when they inform and never move. What makes a public message actually stick — and change behaviour.',
    img: 'https://readdy.ai/api/search-image?query=Editorial%20abstract%20photograph%20of%20colorful%20awareness%20posters%20and%20graphic%20prints%20arranged%20on%20a%20wall%2C%20warm%20amber%20and%20deep%20green%20palette%2C%20creative%20public%20outreach%20mood%2C%20soft%20lighting%2C%20no%20text%20no%20people%20no%20logos&width=800&height=520&seq=prabhaw-insight-05&orientation=landscape',
    body: [
      {
        type: 'paragraph',
        text: 'Information, education and communication — the familiar trio of public outreach — has a quiet problem. It is excellent at informing and surprisingly poor at changing anything. A message that people understand but do not act on has informed without communicating. The difference is the entire craft.',
      },
      {
        type: 'heading',
        text: 'Awareness is not behaviour',
      },
      {
        type: 'paragraph',
        text: 'Every campaign can raise awareness. Almost no campaign, on its own, changes behaviour. Behaviour is not changed by facts; it is changed by the removal of barriers, the presence of habit, and the pull of social proof. A poster can tell a family why vaccination matters, but it cannot walk them to the centre, wait with them, or make the centre welcome when they arrive.',
      },
      {
        type: 'paragraph',
        text: 'The campaigns that work treat information as the first step, not the last. They pair the message with the means: a helpline, a nearby service, a trusted local voice, a moment when the recommended action is easy. Information moves people only when the path from understanding to action is short.',
      },
      {
        type: 'quote',
        text: 'People do not need to be told what is right. They need to be helped to do what is right. That help is the real work of a campaign.',
      },
      {
        type: 'heading',
        text: 'The principles that make messages stick',
      },
      {
        type: 'list',
        items: [
          'Lead with the benefit people already care about, not the system\u2019s priority',
          'Use the language of the street, not the office — tested, not assumed',
          'Show one clear action, repeated consistently across every channel',
          'Put a trusted local face on the message',
          'Make the next step obvious and within reach',
        ],
      },
      {
        type: 'heading',
        text: 'Measuring what matters',
      },
      {
        type: 'paragraph',
        text: 'A campaign should be measured by the behaviour it moves, not the reach it claims. Reach is vanity; the number of people who took the recommended action is the signal. This is harder to measure and forces humility: many well-designed campaigns discover, in the honest data, that they moved far fewer people than their impressions suggested.',
      },
      {
        type: 'paragraph',
        text: 'That humility is the point. The discipline of measuring behaviour turns communication from a broadcast into an experiment — one that learns, iterates, and eventually earns the trust it asks for.',
      },
    ],
  },
  {
    slug: 'constituency-intelligence',
    cat: 'Governance',
    title: 'Constituency intelligence: mapping need before designing response',
    meta: 'Analysis · 10 min read',
    author: 'Prabhaw Research Desk',
    date: 'January 2025',
    minutes: 10,
    excerpt:
      'Response designed without a map of need is guesswork. How systematic constituency mapping turns scattered signals into a defensible plan of action.',
    img: 'https://readdy.ai/api/search-image?query=Editorial%20abstract%20photograph%20of%20a%20map%20with%20marked%20points%20and%20data%20notes%20beside%20a%20laptop%20on%20a%20warm%20desk%2C%20deep%20green%20and%20amber%20tones%2C%20strategic%20field%20intelligence%20mood%2C%20soft%20natural%20light%2C%20no%20text%20no%20people%20no%20logos&width=800&height=520&seq=prabhaw-insight-06&orientation=landscape',
    body: [
      {
        type: 'paragraph',
        text: 'Every constituency is a living map of need — and every response to it is a bet on how well that map is read. The public figures who respond well rarely do so by intuition alone. They work from a patient, systematic picture of where need concentrates, who is underserved, and what the gap between services and experience actually looks like on the ground.',
      },
      {
        type: 'heading',
        text: 'The map before the plan',
      },
      {
        type: 'paragraph',
        text: 'The discipline begins by refusing to plan before mapping. Which habitations are hardest to reach? Which services are nominally present but practically absent? Where do complaints cluster, and where does silence mean satisfaction or simply resignation? A map that answers these questions is worth more than any number of well-intentioned programmes.',
      },
      {
        type: 'paragraph',
        text: 'Mapping is not a single survey. It is an assembly of signals: administrative records, field observation, community conversations, service-point data, and the quiet texture that only local presence reveals. The craft is in the synthesis — finding the pattern across sources that no single source shows.',
      },
      {
        type: 'quote',
        text: 'Intelligence is not the accumulation of information. It is the courage to let a coherent picture emerge — and the humility to revise it when the ground disagrees.',
      },
      {
        type: 'heading',
        text: 'From map to response',
      },
      {
        type: 'list',
        items: [
          'Prioritise by concentration: serve where need is dense and repeated',
          'Target the gaps between scheme and experience, not just scheme vs. scheme',
          'Design for the last mile, where delivery usually breaks',
          'Build feedback so the map is corrected by the people it serves',
        ],
      },
      {
        type: 'heading',
        text: 'The living map',
      },
      {
        type: 'paragraph',
        text: 'The most valuable constituency maps are never finished. They are living documents, corrected by every delivery, every complaint, every field visit. A leader who reads a living map stays ahead of need rather than chasing it — and earns the reputation of someone who always seems to know what matters before it becomes a demand.',
      },
      {
        type: 'paragraph',
        text: 'That is the quiet power of constituency intelligence: it does not predict the future, but it makes the present legible enough that good response becomes the obvious thing to do.',
      },
    ],
  },
];

export function getInsightBySlug(slug: string): InsightArticle | undefined {
  return insights.find((a) => a.slug === slug);
}

export function getRelatedInsights(slug: string, count = 2): InsightArticle[] {
  const current = getInsightBySlug(slug);
  const base = insights.filter((a) => a.slug !== slug);
  if (!current) return base.slice(0, count);
  const sameCat = base.filter((a) => a.cat === current.cat);
  const others = base.filter((a) => a.cat !== current.cat);
  return [...sameCat, ...others].slice(0, count);
}